import { Description, Field } from "@headlessui/react";
import { useEffect, useRef, useState } from "react";
import { Button, Modal, TextInput, Typography } from "@/components/atoms";

const FLIGHT_NUMBER_MAX_LENGTH = 8;

/**
 * Clients type the digits alone ("123"), which the flight lookup on the date screen can't resolve — it
 * needs the airline code. A reminder only: nothing here validates the format, because the real check is
 * the server lookup a step later and this modal can't judge a number it never looks up.
 */
const PREFIX_HINT = "Include the airline code at the start, not just the number.";

interface FlightDetailsModalProps {
  open: boolean;
  /** Pre-fills the input when reopening for a pickup that already has a flight number. */
  initialValue?: string;
  /** Continue — commit the entered flight number. */
  onConfirm: (flightNumber: string) => void;
  /**
   * Not Flying — the DELIBERATE press. On the pickup the caller records the pickup as not flying
   * (DEV-1990), clearing any captured flight; on the drop-off it only clears the departure flight.
   */
  onNotFlying: () => void;
  /**
   * Which leg is being captured (DEV-1487). Only the description changes: the arrival copy promises to
   * align the pickup with the flight's ARRIVAL, which is the opposite of what a drop-off booking does
   * with the number — it works backwards from the departure to a suggested pickup. Defaults to the
   * pickup so every existing call site is unchanged.
   */
  target?: "pickup" | "destination";
}

/**
 * Input-only Flight Details modal: captures a free-text flight number for an airport pickup so the
 * pickup can later be aligned to the flight's arrival. No flight lookup happens here — the flight is
 * resolved to details later, on the date screen.
 *
 * ## THE BUTTONS ARE THE ONLY WAY OUT (Justin, 18 Sep 2026)
 *
 * `onClose` is a deliberate no-op, so a backdrop click and Escape both do nothing. The question has
 * exactly two answers — a flight number, or "I'm not flying" — and both are reachable at all times
 * because `Not Flying` is never disabled. Nobody is trapped; they are just made to answer.
 *
 * This replaced an `onDismiss` prop whose whole job was making an incidental close SAFE: `onNotFlying`
 * is destructive on edit (it clears the captured flight), so a stray Escape wired to the dialog's
 * `onClose` would silently throw away a number the client never meant to drop. With no incidental close
 * left to route, that hazard and the prop that guarded it are both gone.
 *
 * It also closes the re-entry gap the row-hiding rule opened: an airport stop with no flight renders no
 * field at all, and `LocationPanel` prompts once per airport (`promptedPickupKey`), so an accidental
 * Escape used to leave a flight booking with no flight and no way back to add one.
 */
const DESCRIPTIONS: Record<NonNullable<FlightDetailsModalProps["target"]>, string> = {
  pickup:
    "Provide the flight number so we can schedule your pickup accordingly. You'll have the option to align the pickup time with the flight's arrival.",
  destination:
    "Provide the flight number so we can schedule your pickup accordingly. We'll suggest a pickup time that gets you to your terminal in time for departure.",
};

export const FlightDetailsModal = ({ open, initialValue = "", onConfirm, onNotFlying, target = "pickup" }: FlightDetailsModalProps) => {
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) setValue(initialValue);
  }, [open, initialValue]);

  const trimmed = value.trim();

  // Drop focus from the flight-number field before closing (Continue / Not Flying — the only two exits)
  // so the input doesn't stay focused (and the mobile keyboard doesn't linger) after the modal hides.
  const handleNotFlying = () => {
    inputRef.current?.blur();
    onNotFlying();
  };
  const handleConfirm = () => {
    inputRef.current?.blur();
    onConfirm(trimmed.toUpperCase());
  };

  return (
    <Modal
      open={open}
      // Inert BY DESIGN — see the docblock. Headless UI's Dialog calls `onClose` for a backdrop click
      // and for Escape, and a no-op is how it is told to ignore both; there is no `static`/dismissible
      // flag on the Dialog to set instead.
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onClose={() => {}}
      variant="web-small"
      title="Flight Details"
      description={DESCRIPTIONS[target]}
      renderButtons={() => (
        <>
          <Button variant="tertiary" size="md" onClick={handleNotFlying}>
            Not Flying
          </Button>
          <Button variant="primary" size="md" disabled={!trimmed} onClick={handleConfirm}>
            Continue
          </Button>
        </>
      )}
    >
      {/* Headless UI's Input owns aria-describedby, so a hand-rolled one is dropped. Field + Description
          is the seam it does honour, and it keeps the wiring correct with both legs mounted at once. */}
      <Field>
        <TextInput
          ref={inputRef}
          iconName="Airplane"
          placeholder="Enter Flight Number (e.g. QF123)"
          maxLength={FLIGHT_NUMBER_MAX_LENGTH}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoFocus
        />
        <Description as={Typography} variant="paragraph" className="mt-1 leading-5 text-neutral-silver-gray">
          {PREFIX_HINT}
        </Description>
      </Field>
    </Modal>
  );
};
